import { type KeyboardEvent, type MouseEvent, useCallback } from 'react';

import { useRouter, useRouterActions } from 'react-resource-router';

import { isModifiedClick } from '@atlassian/twg-microsite-common/is-modified-click';

import { setHash } from '../../../common/deep-link/setHash';
import { HOME_ROUTE_NAME } from '../../../routes';
import { anchorHref } from './navigationItems';

/**
 * Shared click behaviour for every section link, called from `AnchorLinkItem`
 * so both anchor menus behave identically: `setHash` scrolls in-page when the
 * visitor is already on the anchor's home route, and `push` navigates there
 * carrying the fragment from anywhere else.
 *
 * Takes a keyboard activation as well as a pointer one, because `LinkItem`
 * invokes its `onClick` for both.
 */
export const useAnchorItemClick = (
	token: string,
): ((event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void) => {
	const [{ route }] = useRouter();
	const { push } = useRouterActions();
	const isOnHome = route?.name === HOME_ROUTE_NAME;

	return useCallback(
		(event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>): void => {
			if (isModifiedClick(event)) {
				return;
			}
			event.preventDefault();

			if (isOnHome) {
				// Pushing the route we're already on is a no-op, so ask
				// `useScrollToHash` (mounted by the Home page) to scroll instead by
				// firing the `hashchange` event it listens for.
				setHash(token);
				return;
			}

			push(anchorHref(token));
		},
		[isOnHome, push, token],
	);
};
